import React from "react";
import styles from "./style.module.scss";
import Loading from "../../../components/TPMS/Loading";
import EventHeader from "../../../components/TPMS/TPMSSummaryPage/EventHeader";
import { useQuery } from "@apollo/client";
import {
  QUERY_STINTS_WITH_TPMS_FOR_ALL_CARS,
  QUERY_DRIVER_NAMES,
  QUERY_SESSION_IDS_FOR_EVENT,
  QUERY_SESSION_DISPLAY_NAME_MAP,
  QUERY_CAR_STATUS,
} from "../../../utils/graphql/queries/TPMSQueries";
import SessionsBar, {
  Session,
} from "../../../components/TPMS/TPMSSummaryPage/SessionsBar/SessionsBar";
import DriverSelector from "../../../components/TPMS/TPMSSummaryPage/DriverMetrics";
import TyreLapChart from "../../../components/TPMS/TPMSSummaryPage/TyreLapChart";
import Modal from "../../../components/TPMS/TPMSSummaryPage/Modal";
import {
  CurrentTyresButton,
  CurrentTyresModal,
} from "../../../components/TPMS/TPMSSummaryPage/CurrentTyres";
import { IdsProvider } from "../../../components/TPMS/TPMSSummaryPage/IdSwitch/IdsContext";
import ModalSessionsList from "../../../components/TPMS/TPMSSummaryPage/Modal/ModalSessionsList";
import AuthenticatedLayout from "../../../components/Layout/authenticatedLayout";

type TPMSFrame = {
  dt: string | Date;
  frontLeft: number;
  frontRight: number;
  rearLeft: number;
  rearRight: number;
};

type RawStint = {
  type: string;
  sesId?: string;
  lapsInStint?: number | null;
  tpmsAtStart?: TPMSFrame | null;
};

type StintAnalysisResult = {
  dt: string | Date;
  carNum: string;
  stints: RawStint[];
};

type DriverName = {
  carNum: string;
  name: string;
  team?: string;
  color?: string;
};

type TyreLapTotal = { id: string; laps: number };

function computeLapTotals(stints: RawStint[]): TyreLapTotal[] {
  const tyreLapTotals: Record<string, number> = {};

  for (const stint of stints) {
    const laps = stint.lapsInStint ?? 0;
    const tpms = stint.tpmsAtStart;
    if (!tpms) continue;

    const tyreIds = [
      tpms.frontLeft,
      tpms.frontRight,
      tpms.rearLeft,
      tpms.rearRight,
    ];

    for (const tyreId of tyreIds) {
      if (!tyreId) continue;
      const idStr = tyreId.toString();
      if (!tyreLapTotals[idStr]) tyreLapTotals[idStr] = 0;
      tyreLapTotals[idStr] += laps;
    }
  }

  return Object.entries(tyreLapTotals).map(([id, laps]) => ({ id, laps }));
}

export default function TPMSSummaryPage() {
  const [eventCode, seteventCode] = React.useState(""); // Start blank
  const [selectedDrivers, setSelectedDrivers] = React.useState<string[]>([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalDriverData, setModalDriverData] = React.useState<{
    name: string;
    carNum: string;
    lapTotals: { id: string; laps: number }[];
    color?: string;
  } | null>(null);
  const [trackLengthKm, setTrackLengthKm] = React.useState<number | "">("");
  const [selectedSessions, setSelectedSessions] = React.useState<string[]>([]);
  const [currentTyresOpen, setCurrentTyresOpen] = React.useState(false);

  // Car status query (must be after eventCode declaration)
  const { data: carStatusData } = useQuery(QUERY_CAR_STATUS, {
    variables: { eventCode },
    skip: !eventCode,
    fetchPolicy: "network-only",
  });

  type CarStatusWithTPMS = {
    status: string | null;
    tpms: TPMSFrame | null;
  };

  // Map carNum to status and tpms
  const carStatusMap: Record<string, CarStatusWithTPMS> = React.useMemo(() => {
    const arr = carStatusData?.carStatus ?? [];
    const map: Record<string, CarStatusWithTPMS> = {};
    for (const entry of arr) {
      map[entry.carNum] = {
        status: entry.status ?? null,
        tpms: entry.tpms ?? null,
      };
    }
    return map;
  }, [carStatusData]);

  // Step 1: Query session IDs for the selected event
  const { data: sessionIdsData, loading: sessionIdsLoading } = useQuery(
    QUERY_SESSION_IDS_FOR_EVENT,
    {
      variables: { eventCode },
      skip: !eventCode,
      fetchPolicy: "network-only",
    },
  );
  const sessionIds: string[] = sessionIdsData?.getSessionIdsForEvent ?? [];

  // Step 2: Query display names for those session IDs
  const { data: sessionNamesData, loading: sessionNamesLoading } = useQuery(
    QUERY_SESSION_DISPLAY_NAME_MAP,
    {
      variables: { sessionIds },
      skip: !eventCode || sessionIds.length === 0,
      fetchPolicy: "network-only",
    },
  );
  type SessionDisplayName = {
    aksSessionId: string;
    displayName: string;
  };

  const sessions: Session[] = React.useMemo(
    () =>
      sessionNamesData?.getSessionDisplayNameMap?.map(
        (s: SessionDisplayName) => ({
          _id: s.aksSessionId,
          session: s.displayName,
          dt: "", // dt not available from this query
        }),
      ) ?? [],
    [sessionNamesData],
  );

  // Select all sessions by default when sessions change
  React.useEffect(() => {
    if (sessions.length > 0) {
      setSelectedSessions(sessions.map((s) => s._id));
    }
  }, [sessions]);

  const { data, loading, error } = useQuery(
    QUERY_STINTS_WITH_TPMS_FOR_ALL_CARS,
    {
      variables: { eventCode },
      skip: !eventCode,
      fetchPolicy: "network-only",
    },
  );

  const { data: driverNamesData } = useQuery(QUERY_DRIVER_NAMES, {
    variables: { eventCode },
    skip: !eventCode,
    fetchPolicy: "network-only",
  });

  const drivers: StintAnalysisResult[] = React.useMemo(
    () => data?.getStintsWithTPMSForAllCars ?? [],
    [data],
  );

  const driverNames: DriverName[] =
    driverNamesData?.getDriverNamesForEvent ?? [];

  const configOptions = (
    <EventHeader
      selectedEvent={eventCode}
      setSelectedEvent={seteventCode}
      trackLengthKm={trackLengthKm}
      setTrackLengthKm={setTrackLengthKm}
      cacheClearDisabled={loading}
    />
  );

  const handleDriverSelectionChange = (selected: string[]) => {
    setSelectedDrivers(selected);
  };

  const handleChartClick = (
    driverName: string,
    carNum: string,
    lapTotals: { id: string; laps: number }[],
    color?: string,
  ) => {
    setModalDriverData({ name: driverName, carNum, lapTotals, color });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalDriverData(null);
  };

  const driverList = drivers
    .map((driver) => {
      const driverName = driverNames.find((d) => d.carNum === driver.carNum);
      return {
        carNum: driver.carNum,
        name: driverName?.name || `Car ${driver.carNum}`,
        team: driverName?.team,
        color: driverName?.color,
      };
    })
    .sort((a, b) => {
      const teamA = a.team || "Unknown";
      const teamB = b.team || "Unknown";
      if (teamA !== teamB) {
        return teamB.localeCompare(teamA);
      }
      return b.carNum.localeCompare(a.carNum);
    });

  const filteredDrivers = (() => {
    const filterStintsBySession = (stints: RawStint[]) =>
      stints.filter(
        (stint) => stint.sesId && selectedSessions.includes(stint.sesId),
      );

    if (selectedDrivers.length > 0) {
      const selectedDriverSet = new Set(selectedDrivers);
      return drivers
        .filter((driver) => selectedDriverSet.has(driver.carNum))
        .map((driver) => {
          const driverInfo = driverList.find((d) => d.carNum === driver.carNum);
          return {
            ...driver,
            team: driverInfo?.team,
            stints: filterStintsBySession(driver.stints),
          };
        })
        .sort((a, b) => {
          const teamA = a.team || "Unknown";
          const teamB = b.team || "Unknown";
          if (teamA !== teamB) {
            return teamB.localeCompare(teamA);
          }
          return b.carNum.localeCompare(a.carNum);
        });
    } else {
      return drivers
        .map((driver) => {
          const driverInfo = driverList.find((d) => d.carNum === driver.carNum);
          return {
            ...driver,
            team: driverInfo?.team,
            stints: filterStintsBySession(driver.stints),
          };
        })
        .sort((a, b) => {
          const teamA = a.team || "Unknown";
          const teamB = b.team || "Unknown";
          if (teamA !== teamB) {
            return teamB.localeCompare(teamA); // Reversed order
          }
          return b.carNum.localeCompare(a.carNum); // Reversed order
        });
    }
  })();

  return (
    <IdsProvider drivers={drivers}>
      <CurrentTyresModal
        isOpen={currentTyresOpen}
        onClose={() => setCurrentTyresOpen(false)}
        eventCode={eventCode}
        driverNames={driverNames}
      />
      <AuthenticatedLayout
        title={"TPMS Summary"}
        configOptions={configOptions}
        showJaguarLogo={false}
      >
        <div className={styles.container}>
          {/* Sessions Bar */}
          {eventCode && (
            <SessionsBar
              sessions={sessions}
              selectedSessions={selectedSessions}
              onSelectionChange={setSelectedSessions}
              loading={sessionIdsLoading || sessionNamesLoading}
              rightElement={
                <CurrentTyresButton onClick={() => setCurrentTyresOpen(true)} />
              }
            />
          )}
          {!eventCode ? (
            <div />
          ) : loading ? (
            <Loading />
          ) : error ? (
            <div>Error loading data</div>
          ) : (
            <>
              <div className={styles.driverSelectorContainer}>
                <DriverSelector
                  drivers={driverList}
                  onSelectionChange={handleDriverSelectionChange}
                />
              </div>

              <div className={styles.driverCardsGrid}>
                {filteredDrivers.map((driver) => {
                  const lapTotals = computeLapTotals(driver.stints);
                  const driverInfo = driverList.find(
                    (d) => d.carNum === driver.carNum,
                  );
                  const displayName =
                    driverInfo?.name || `Car ${driver.carNum}`;
                  const carStatusEntry = carStatusMap[driver.carNum] ?? {};
                  const carStatus = carStatusEntry.status ?? "";

                  let mostRecentStint = null;
                  if (driver.stints && driver.stints.length > 0) {
                    mostRecentStint = [...driver.stints]
                      .filter((s) => s.tpmsAtStart)
                      .sort((a, b) => {
                        // dt can be string or Date
                        const dateA = a.tpmsAtStart
                          ? new Date(a.tpmsAtStart.dt).getTime()
                          : 0;
                        const dateB = b.tpmsAtStart
                          ? new Date(b.tpmsAtStart.dt).getTime()
                          : 0;
                        return dateB - dateA;
                      })[0];
                  }
                  let highlightTyreIds: string[] = [];
                  if (mostRecentStint && mostRecentStint.tpmsAtStart) {
                    const tpms = mostRecentStint.tpmsAtStart;
                    highlightTyreIds = [
                      tpms.frontLeft,
                      tpms.frontRight,
                      tpms.rearLeft,
                      tpms.rearRight,
                    ]
                      .filter((id) => id !== undefined && id !== null)
                      .map((id) => id.toString());
                  }

                  return (
                    <div key={driver.carNum} className={styles.driverCard}>
                      <div className={styles.tyreLapData}>
                        <TyreLapChart
                          lapTotals={lapTotals}
                          driverName={displayName}
                          driverCarNum={driver.carNum}
                          driverColor={driverInfo?.color}
                          trackLengthKm={trackLengthKm}
                          carStatus={carStatus}
                          highlightTyreIds={highlightTyreIds}
                          onChartClick={() =>
                            handleChartClick(
                              displayName,
                              driver.carNum,
                              lapTotals,
                              driverInfo?.color,
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <Modal
          isOpen={modalOpen}
          onClose={handleModalClose}
          driverName={modalDriverData?.name || ""}
          driverCarNum={modalDriverData?.carNum || ""}
        >
          {(() => {
            if (!modalDriverData) return null;
            const selectedDriver = drivers.find(
              (d) => d.carNum === modalDriverData.carNum,
            );
            if (!selectedDriver) return <div>No session data available.</div>;
            return (
              <div style={{ minHeight: "200px" }}>
                <ModalSessionsList
                  carNum={selectedDriver.carNum}
                  sessionsList={[]}
                  stints={selectedDriver.stints
                    .filter(
                      (stint) =>
                        stint.sesId && selectedSessions.includes(stint.sesId),
                    )
                    .map((stint) => ({
                      ...stint,
                      lapsInStint: stint.lapsInStint ?? undefined,
                      tpmsAtStart: stint.tpmsAtStart
                        ? {
                            ...stint.tpmsAtStart,
                            dt:
                              typeof stint.tpmsAtStart.dt === "string"
                                ? stint.tpmsAtStart.dt
                                : (stint.tpmsAtStart.dt as Date).toISOString(),
                          }
                        : null,
                    }))}
                  allEventStints={selectedDriver.stints.map((stint) => ({
                    ...stint,
                    lapsInStint: stint.lapsInStint ?? undefined,
                    tpmsAtStart: stint.tpmsAtStart
                      ? {
                          ...stint.tpmsAtStart,
                          dt:
                            typeof stint.tpmsAtStart.dt === "string"
                              ? stint.tpmsAtStart.dt
                              : (stint.tpmsAtStart.dt as Date).toISOString(),
                        }
                      : null,
                  }))}
                />
              </div>
            );
          })()}
        </Modal>
      </AuthenticatedLayout>
    </IdsProvider>
  );
}
